import {
  useState,
  createContext,
  useContext,
  useEffect,
  ReactNode,
  useMemo,
} from 'react';
import { useTranslation, TFunction } from 'react-i18next';
import { Typography } from '@kizen/kds/Typography';
import Switch from '__components/Kizen/Switch';
import Input from '__components/Kizen/Input';
import Select from '__components/Kizen/Select/Outlined';
import {
  fontSizeOptions,
  borderWeightOptions,
  borderWeightLookup,
} from 'components/PageBuilder/options';
import { FlexUnderlineMultiSelect } from 'components/PageBuilder/settings/components';
import ColorPickerInput from '__components/Kizen/ColorPickerInput';
import {
  useFontOptionLookup,
  useFonts,
} from '__components/PageBuilder/settings/useFonts';
import { rgbaToHex } from './utils';
import { DashboardStyleSetting, DashboardStyleSettingContext } from './types';
import { layers } from '__app/spacing';
import { StyledLabel } from '__components/Kizen/Select/styles';
import { gray } from '@kizen/kds/Colors';
import { useVizCatColors } from 'pages/Dashboard/components/useVizCatColors';

export const DEFAULT_DASHLET_STYLE_SETTINGS = {
  dropShadow: false,
  titleAllCaps: true,
  boldTitle: false,
  fontFamily: 'Proxima Nova',
  italicTitle: false,
  titleFontSize: '12',
  titleLineHeight: 2.5,
  topLeftBorderRadius: 4,
  topRightBorderRadius: 4,
  bottomLeftBorderRadius: 4,
  bottomRightBorderRadius: 4,
  borderColor: gray['gray-50'],
  borderWeight: 0,
} as DashboardStyleSetting;

export const lineHeightOptions = [
  { value: 1, label: '1em' },
  { value: 1.25, label: '1.25em' },
  { value: 1.5, label: '1.5em' },
  { value: 1.75, label: '1.75em' },
  { value: 2, label: '2em' },
  { value: 2.5, label: '2.5em' },
  { value: 3, label: '3em' },
];

export const titleDecorationOptions = (t: TFunction) => [
  {
    value: 'bold',
    label: t('B'),
  },
  {
    value: 'italic',
    label: t('I'),
  },
];

export const getLookup = (options: { value: string; label: string }[]) =>
  options.reduce(
    (acc: Record<string, { value: string; label: string }>, opt) => {
      acc[opt.value] = opt;
      return acc;
    },
    {}
  );

export const lineHeightOptionsLookup = lineHeightOptions.reduce(
  (acc: Record<number, { value: number; label: string }>, opt) => {
    acc[opt.value] = opt;
    return acc;
  },
  {}
);

export const DashboardStyleSettingsContext = createContext<
  DashboardStyleSettingContext | undefined
>(undefined);

export const DashboardStyleSettingsProvider = ({
  styleSettings,
  children,
}: {
  styleSettings: DashboardStyleSetting;
  children: ReactNode;
}) => {
  const [settings, setSettings] = useState<DashboardStyleSetting>(
    Object.keys(styleSettings).length > 0
      ? styleSettings
      : DEFAULT_DASHLET_STYLE_SETTINGS
  );

  const updateSettings = (updates: Partial<DashboardStyleSetting>) => {
    setSettings((prev) => ({ ...prev, ...updates }));
  };

  useEffect(() => {
    setSettings(styleSettings);
  }, [styleSettings]);

  return (
    <DashboardStyleSettingsContext.Provider
      value={{ settings, updateSettings }}
    >
      {children}
    </DashboardStyleSettingsContext.Provider>
  );
};

export const useDashboardSettings = () => {
  const context = useContext(DashboardStyleSettingsContext);
  return context;
};

export const MAX_BORDER_RADIUS = 20;

export const DashboardStyleSettings = ({
  onChange,
  inModal = true,
  hideChartTheme = false,
}: {
  onChange: (settings: DashboardStyleSetting) => void;
  inModal?: boolean;
  hideChartTheme?: boolean;
}) => {
  const { t } = useTranslation();
  const fonts = useFonts();
  const fontOptionLookup = useFontOptionLookup(fonts);
  const context = useContext(DashboardStyleSettingsContext);
  const defaultChartTheme = useVizCatColors();

  if (!context) {
    throw new Error(
      'DashboardStyleSettingsContext must be used within a DashboardStyleSettingsProvider'
    );
  }
  const { settings, updateSettings } = context;
  const {
    dropShadow,
    topLeftBorderRadius,
    topRightBorderRadius,
    bottomLeftBorderRadius,
    bottomRightBorderRadius,
    borderColor,
    borderWeight,
    titleAllCaps,
    titleFontSize,
    titleLineHeight,
    titleColor,
    titleBackgroundColor,
    boldTitle,
    italicTitle,
    fontFamily = '',
    chartTheme = defaultChartTheme,
  } = settings;

  const handleChange = (updates: Partial<DashboardStyleSetting>) => {
    updateSettings(updates);
    onChange({ ...settings, ...updates });
  };

  const showInvalidBorderRadiusNumber =
    Number(topLeftBorderRadius) > MAX_BORDER_RADIUS ||
    Number(topRightBorderRadius) > MAX_BORDER_RADIUS ||
    Number(bottomLeftBorderRadius) > MAX_BORDER_RADIUS ||
    Number(bottomRightBorderRadius) > MAX_BORDER_RADIUS;

  const showInvalidBorderRadiusInput =
    isNaN(Number(topLeftBorderRadius)) ||
    isNaN(Number(topRightBorderRadius)) ||
    isNaN(Number(bottomLeftBorderRadius)) ||
    isNaN(Number(bottomRightBorderRadius));

  const fontFamilyOptions: { value: string; label: string }[] = useMemo(
    () =>
      [
        {
          value: "'Proxima Nova',sans-serif",
          label: DEFAULT_DASHLET_STYLE_SETTINGS.fontFamily,
        },
        ...(fonts as { value: string; label: string }[]),
      ].sort((a, b) => a.label.localeCompare(b.label)),
    [fonts]
  );

  const fontFamilyLookupWithDashletDefault = useMemo(
    () => ({
      ...fontOptionLookup,
      'Promixma Nova': {
        value: "'Proxima Nova',sans-serif",
        label: 'Promixma Nova',
      },
    }),
    [fontOptionLookup]
  );

  const titleDecorationValue = useMemo(() => {
    const results = [];
    if (boldTitle) {
      results.push('bold');
    }

    if (italicTitle) {
      results.push('italic');
    }
    return results;
  }, [boldTitle, italicTitle]);

  return (
    <div className="flex flex-col" data-qa="style-settings">
      <div className="mt-[40px]">
        <Typography
          className="mb-[20px]"
          size="lg"
          variant="header"
          weight="semibold"
        >
          {t('Title Bar Styling')}
        </Typography>
        <div className="grid grid-cols-[200px,1fr,1fr,1fr,1fr] gap-x-[25px] gap-y-[20px] items-start">
          <div className="mt-[15px]">
            <Select
              wrapperDataQa="title-font-size-select"
              fullWidth
              options={fontSizeOptions}
              value={titleFontSize}
              onChange={(option: any) => {
                handleChange({ titleFontSize: option.value });
              }}
              label={t('Title Font Size')}
            />
          </div>
          <div className="mt-[15px]">
            <ColorPickerInput
              id="title-color"
              label={t('Title Text Color')}
              color={titleColor ?? gray['gray-800']}
              onChange={(color: string) => {
                handleChange({ titleColor: color });
              }}
              zIndex={layers.modals(0, 1)}
              onOpen={() => {}}
              asOverlay
              align="left"
              mini
              disableAlpha
            />
          </div>
          <div className="mt-[15px]">
            <Select
              wrapperDataQa="title-line-height-select"
              fullWidth
              options={lineHeightOptions}
              value={getLookup(lineHeightOptions as any)[titleLineHeight]}
              onChange={(option: { value: string }) => {
                handleChange({ titleLineHeight: Number(option.value) });
              }}
              label={t('Title Line Height')}
            />
          </div>
          <div className="mt-[15px]">
            <Select
              wrapperDataQa="title-font-family-select"
              fullWidth
              options={fontFamilyOptions}
              value={
                fontFamilyLookupWithDashletDefault[fontFamily] ??
                fontFamilyLookupWithDashletDefault['Promixma Nova']
              }
              onChange={(option: any) => {
                handleChange({ fontFamily: option.value });
              }}
              label={t('Title Font Family')}
            />
          </div>
          <div className="mt-[15px]">
            <div className="flex flex-col">
              <StyledLabel>{t('Title Decoration')}</StyledLabel>
              <div className="flex items-start">
                <FlexUnderlineMultiSelect
                  variant="outline"
                  fontSize="text"
                  data-qa-id="title-decoration"
                  value={titleDecorationValue as any}
                  options={titleDecorationOptions(t)}
                  onChange={(values: any) => {
                    handleChange({
                      boldTitle: values.includes('bold'),
                      italicTitle: values.includes('italic'),
                    });
                  }}
                  width="73px"
                />
              </div>
            </div>
          </div>
          <div className="mt-[15px]">
            <Switch
              data-qa="all-caps-title"
              textPlacement="regular-top"
              checked={titleAllCaps ?? true}
              onChange={(event: any) =>
                handleChange({ titleAllCaps: event.target.checked })
              }
              label={t('All Caps Title')}
            />
          </div>
          <div className="mt-[15px]">
            <ColorPickerInput
              id="background-color"
              label={t('Background Color')}
              color={titleBackgroundColor ?? 'rbga(0,0,0,0)'}
              zIndex={layers.modals(1, 3)}
              onChange={(color: string) =>
                handleChange({ titleBackgroundColor: color })
              }
              onOpen={() => {}}
              align="left"
              asOverlay
              mini
              disableAlpha
            />
          </div>
          <div className="mt-[30px]"></div>
        </div>
      </div>
      <div className="mt-[40px]">
        <Typography
          className="mb-[20px]"
          size="lg"
          variant="header"
          weight="semibold"
        >
          {t('Block Styling')}
        </Typography>
        <div className="grid grid-cols-[195px,1fr,1fr,1fr,1fr] gap-x-[25px] gap-y-[20px] items-start">
          <div className="mt-[16px] mb-[15px] flex flex-col">
            <Typography>{t('Border Radius (px)')}</Typography>
            <div className="flex gap-x-[10px] gap-y-[10px] mt-[15px] text-center items-start">
              <div className="flex flex-col gap-[5px] pr-[2px]">
                <Input
                  width="42px"
                  type="ONLY_NUMBER"
                  compact
                  data-qa-id="top-left-border-radius"
                  value={topLeftBorderRadius}
                  onChange={(value: string) => {
                    handleChange({
                      topLeftBorderRadius:
                        value === '' ? undefined : Number(value),
                    });
                  }}
                  maxLength="2"
                  error={topLeftBorderRadius > MAX_BORDER_RADIUS}
                />
                <Typography>{t('Top-Lt')}</Typography>
              </div>
              <div className="flex flex-col items-start gap-[5px]">
                <Input
                  width="42px"
                  type="ONLY_NUMBER"
                  compact
                  data-qa-id="top-right-border-radius"
                  value={topRightBorderRadius}
                  onChange={(value: any) => {
                    handleChange({
                      topRightBorderRadius:
                        value === '' ? undefined : Number(value),
                    });
                  }}
                  maxLength="2"
                  error={topRightBorderRadius > MAX_BORDER_RADIUS}
                />
                <Typography>{t('Top-Rt')}</Typography>
              </div>
              <div className="flex flex-col items-start gap-[5px]">
                <Input
                  width="42px"
                  type="ONLY_NUMBER"
                  compact
                  data-qa-id="bot-left-border-radius"
                  value={bottomLeftBorderRadius}
                  onChange={(value: any) => {
                    handleChange({
                      bottomLeftBorderRadius:
                        value === '' ? undefined : Number(value),
                    });
                  }}
                  maxLength="2"
                  error={bottomLeftBorderRadius > MAX_BORDER_RADIUS}
                />
                <Typography>{t('Bot-Lt')}</Typography>
              </div>
              <div className="flex flex-col  items-start  gap-[5px]">
                <Input
                  width="42px"
                  type="ONLY_NUMBER"
                  compact
                  data-qa-id="bot-right-border-radius"
                  value={bottomRightBorderRadius}
                  onChange={(value: any) => {
                    handleChange({
                      bottomRightBorderRadius:
                        value === '' ? undefined : Number(value),
                    });
                  }}
                  maxLength="2"
                  error={bottomRightBorderRadius > MAX_BORDER_RADIUS}
                />
                <Typography>{t('Bot-Rt')}</Typography>
              </div>
            </div>
            <div className="relative">
              {showInvalidBorderRadiusNumber ? (
                <div className="mt-[10px] absolute">
                  <Typography size="sm" color="feedback-error">
                    {t('Border radius cannot be greater than {{max}}px', {
                      max: MAX_BORDER_RADIUS,
                    })}
                  </Typography>
                </div>
              ) : null}

              {showInvalidBorderRadiusInput &&
              !showInvalidBorderRadiusNumber ? (
                <div className="mt-[10px] absolute">
                  <Typography size="sm" color="feedback-error">
                    {t('Border radius must be a valid number')}
                  </Typography>
                </div>
              ) : null}
            </div>
          </div>
          <div className="mt-[15px]">
            <Select
              fullWidth
              wrapperDataQa="style-settings-border-weight-select"
              options={borderWeightOptions}
              value={
                (borderWeightLookup as Record<number, string>)[borderWeight]
              }
              onChange={(option: any) => {
                handleChange({ borderWeight: Number(option.value) });
              }}
              label={t('Border Weight')}
            />
          </div>
          <div className="mt-[15px]">
            <ColorPickerInput
              id="border-color"
              zIndex={layers.modals(1, 3)}
              asOverlay
              label={t('Border Color')}
              color={borderColor ?? 'rbga(0,0,0,0)'}
              onChange={(color: string) => handleChange({ borderColor: color })}
              onOpen={() => {}}
              align="right"
              mini
              disableAlpha
            />
          </div>
          <div className="mt-[15px]">
            <Switch
              textPlacement="regular-top"
              checked={dropShadow ?? false}
              onChange={(event: any) =>
                handleChange({ dropShadow: event.target.checked })
              }
              label={t('Drop Shadow')}
            />
          </div>
        </div>
      </div>
      {!hideChartTheme && (
        <div className="mt-[40px]">
          <Typography
            className="mb-[20px]"
            size="lg"
            variant="header"
            weight="semibold"
          >
            {t('Chart Theme')}
          </Typography>
          <div className="grid grid-cols-[200px,1fr,1fr,1fr,1fr] gap-x-[25px] gap-y-[20px] items-start">
            {defaultChartTheme.map((_, index) => {
              return (
                <div className="mt-[15px]" key={`chart-theme-${index + 1}`}>
                  <ColorPickerInput
                    id={`theme-color-${index + 1}`}
                    label={t('Color {{themeColorNumber}}', {
                      themeColorNumber: index + 1,
                    })}
                    color={
                      (chartTheme[index] ?? defaultChartTheme[index]).light
                    }
                    zIndex={layers.modals(1, 10)}
                    onChange={(color: string) => {
                      const hexColor =
                        rgbaToHex(color) ??
                        (chartTheme[index] ?? defaultChartTheme[index]).light;
                      const updatedThemes = defaultChartTheme.map(
                        (themeColor, themeIndex) => {
                          if (themeIndex === index) {
                            return { light: hexColor };
                          } else if (chartTheme[themeIndex]) {
                            return chartTheme[themeIndex];
                          }
                          return themeColor;
                        }
                      );
                      handleChange({
                        chartTheme: updatedThemes,
                      });
                    }}
                    asOverlay
                    onOpen={() => {}}
                    align={index % 5 < 2 ? 'left' : 'right'}
                    mini
                    disableAlpha
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
